import React, { useEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import { useLocation, useParams } from "react-router";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { label } from "../../config";
import config from "../../env.config";
import { getUserAIEnabledVariables } from "../../shared/services/auth.service";
import { sendAuthTokenToIframe } from "../../utils/setAuthorizationToken";

const Chatbot = ({ userId, organizationId, baseUrl, userName, domainName }) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const draggableRef = useRef(null);
  const iframeRef = useRef(null);
  const targetOrigin = config.BASE_URL.CHATBOT_URL;
  const [userContext, setUserContext] = useState(null);
  const { pathname } = useLocation();
  const currentPath = pathname.replace(/^\//, "").split("/")[0] || pathname;
  const previousPathRef = useRef(currentPath);

  const iframeSrc = `${
    config.BASE_URL.CHATBOT_URL
  }/chatbot/?userId=${userId}&organizationId=${organizationId}&baseUrl=${baseUrl}&userName=${userName}&domainName=${domainName}&currentPath=${currentPath}&userContext=${JSON.stringify(
    userContext
  )}`;

  useEffect(() => {
    const fetchUserContext = async () => {
      const userContextData = await getUserAIEnabledVariables({
        companyId: organizationId,
        drutasUserId: userId,
      });
      if (userContextData) {
        setUserContext(userContextData);
      }
    };
    fetchUserContext();
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;

    if (iframe) {
      iframe.onload = async () => {
        const token = sendAuthTokenToIframe();
        iframe.contentWindow.postMessage({ token, type: "auth" }, targetOrigin);
      };
    }

    return () => {
      if (iframe) iframe.onload = null;
    };
  }, [iframeRef.current]);

  useEffect(() => {
    const handleMessage = (event) => {
      // Security: verify origin matches chatbot URL
      if (event.origin !== targetOrigin) return;

      if (event.data && typeof event.data.chatOpen !== "undefined") {
        setIsChatOpen(event.data.chatOpen);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [targetOrigin]);

  useEffect(() => {
    if (!isChatOpen) {
      setPosition({ x: 0, y: 0 });
    }
  }, [isChatOpen]);

  useEffect(() => {
    if (currentPath !== previousPathRef.current) {
      if (isChatOpen) {
        // Send close message to iframe
        if (iframeRef.current && iframeRef.current.contentWindow) {
          iframeRef.current.contentWindow.postMessage({ type: "closeChat" }, targetOrigin);
        }
        // Close immediately in parent
        setIsChatOpen(false);
        setPosition({ x: 0, y: 0 });
      }
      previousPathRef.current = currentPath;
    }
  }, [currentPath, isChatOpen, targetOrigin]);

  const handleDragStart = (e) => {
    document.body.style.userSelect = "none";
    document.body.style.webkitUserSelect = "none";
    document.body.style.msUserSelect = "none";
    document.body.style.MozUserSelect = "none";
    document.body.style.cursor = "move";
  };

  const handleDrag = (e, data) => {
    setPosition({ x: data.x, y: data.y });
  };

  const handleDragStop = (e, data) => {
    document.body.style.userSelect = "";
    document.body.style.webkitUserSelect = "";
    document.body.style.msUserSelect = "";
    document.body.style.MozUserSelect = "";
    document.body.style.cursor = "";
    setPosition({ x: data.x, y: data.y });
  };

  const getBounds = () => {
    const chatWidth = 750;
    const chatHeight = 690;
    const headerHeight = 45;
    const minVisibleHeader = 10;

    const maxDownwardMovement = chatHeight - headerHeight;

    const maxUpwardMovement = -(window.innerHeight - chatHeight - minVisibleHeader);

    return {
      left: -75,
      top: maxUpwardMovement,
      right: window.innerWidth - chatWidth + 75,
      bottom: maxDownwardMovement,
    };
  };

  return (
    <>
      <Tooltip anchorElement='target' parentTitle={true} position='right' className={isChatOpen && "hide"}>
        <Draggable
          disabled={!isChatOpen}
          ref={draggableRef}
          position={isChatOpen ? position : { x: 0, y: 0 }}
          onStart={handleDragStart}
          onDrag={handleDrag}
          onStop={handleDragStop}
          bounds={isChatOpen ? getBounds() : undefined}
          handle='.chatbot-drag-handle'
          grid={[1, 1]}
          enableUserSelectHack={true}>
          <div
            style={{
              position: "fixed",
              bottom: isChatOpen ? "0px" : "37px",
              left: isChatOpen ? "75px" : "10px",
              width: isChatOpen ? "750px" : "46px",
              height: isChatOpen ? "690px" : "46px",
              transition: isChatOpen ? "none" : "all 0.3s ease-in-out",
              cursor: isChatOpen ? "default" : "pointer",
              userSelect: "none",
              WebkitUserSelect: "none",
              MozUserSelect: "none",
              msUserSelect: "none",
            }}>
            {isChatOpen && (
              <div
                className='chatbot-drag-handle'
                style={{
                  position: "absolute",
                  top: "0px",
                  left: "0px",
                  width: "65%",
                  height: "45px",
                  cursor: "move",
                  background: "transparent",
                  zIndex: 1000,
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  MozUserSelect: "none",
                  msUserSelect: "none",
                  touchAction: "none",
                }}
              />
            )}
            <iframe
              ref={iframeRef}
              src={iframeSrc}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                zIndex: 0,
              }}
              title={label.HELP_SUPPORTICON}
            />
          </div>
        </Draggable>
      </Tooltip>
    </>
  );
};

export default Chatbot;
